import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Connection } from '../types/connection';
import { connectionsApi } from '../services/api/connections';
import { queryKeys } from '../lib/queryKeys';

export interface ConnectionDataOptions {
  partnerId?: string;
  autoSubscribe?: boolean;
  onConnectionUpdate?: (connection: Connection) => void;
  onConnectionTestResult?: (connectionId: string, result: any) => void;
}

export interface ConnectionDataReturn {
  /** THUB-298: epoch ms of when this query's data last arrived, for a truthful "last updated". */
  dataUpdatedAt?: number;   // optional: test mocks and other callers need not supply it
  connections: Connection[];
  loading: boolean;
  error: string | null;
  getConnection: (id: string) => Connection | undefined;
  getConnectionsByPartner: (partnerId: string) => Connection[];
  refreshConnections: () => void;
  subscribeToConnection: (connectionId: string) => () => void;
  unsubscribeFromConnection: (connectionId: string) => void;
}

export const useConnectionData = (options: ConnectionDataOptions = {}): ConnectionDataReturn => {
  const queryClient = useQueryClient();

  const { data: connections = [], isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.connections.all,
    queryFn: () => connectionsApi.getConnections(),
  });

  const getConnection = useCallback((id: string): Connection | undefined => {
    return connections.find((connection: Connection) => connection.connectionId === id);
  }, [connections]);

  const getConnectionsByPartner = useCallback((partnerId: string): Connection[] => {
    return connections.filter((connection: Connection) => connection.partnerId === partnerId);
  }, [connections]);

  const refreshConnections = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
  }, [queryClient]);

  const subscribeToConnection = useCallback((_connectionId: string): (() => void) => {
    return () => {};
  }, []);

  const unsubscribeFromConnection = useCallback((_connectionId: string) => {}, []);

  return {
    connections,
    loading: isLoading,
    // THUB-298: when this data actually last arrived, so consumers can show a truthful
    // "last updated" without keeping their own timestamp in step by effect.
    dataUpdatedAt,
    error: error ? (error as Error).message : null,
    getConnection,
    getConnectionsByPartner,
    refreshConnections,
    subscribeToConnection,
    unsubscribeFromConnection,
  };
};
